import { Component, input, InputSignal } from '@angular/core';

import { CardsContentGroupSection } from '@core/cms/landing.cms';
import { ModuleChip } from '@shared/ui/module-chip/module-chip';
import { TrustHtmlPipe } from '@shared/pipes/trust-html/trust-html.pipe';

/**
 * Renders a `ComponentSectionCardsContentGroup` using the home `app-widget-to-platform` layout: a
 * row of overlapping "construction-kit" cards. Per card, `title` is the caption, `content` the body,
 * and `itemsList` the module tag chips; a dark `backgroundColorHex` flips the card to the midnight
 * treatment.
 */
@Component({
    selector: 'cms-cards-content-group',
    imports: [ModuleChip, TrustHtmlPipe],
    templateUrl: './cards-content-group.html',
    styleUrl: './cards-content-group.scss',
})
export class CmsCardsContentGroup {
    public readonly section: InputSignal<CardsContentGroupSection> =
        input.required<CardsContentGroupSection>();

    /** Whether a card's authored background colour is dark enough to need light text (Rec. 601 luma). */
    protected isDark(hex: string | null | undefined): boolean {
        if (!hex) {
            return false;
        }

        const value: string = hex.replace('#', '');
        const expanded: string =
            value.length === 3 || value.length === 4
                ? value
                      .split('')
                      .map((channel: string): string => channel + channel)
                      .join('')
                : value;

        // 8-digit `#RRGGBBAA` (and its 4-digit short form) is what colour pickers emit; drop the
        // alpha and judge the colour itself, rather than treating the value as unparseable.
        const full: string = expanded.length === 8 ? expanded.slice(0, 6) : expanded;

        if (full.length !== 6 || !/^[0-9a-f]{6}$/i.test(full)) {
            return false;
        }

        const red: number = Number.parseInt(full.slice(0, 2), 16);
        const green: number = Number.parseInt(full.slice(2, 4), 16);
        const blue: number = Number.parseInt(full.slice(4, 6), 16);

        return (red * 299 + green * 587 + blue * 114) / 1000 < 140;
    }
}
